import React, { useState } from "react";
import { NavLink, useNavigate, useLocation, Outlet } from "react-router-dom";
import { TotalReachLogo } from "./Logo";
import { OrgSwitcher } from "./OrgSwitcher";
import { AiDisclaimerGate } from "./AiDisclaimer";
import { AskTotalReachWidget } from "./AskTotalReachWidget";
import { usePlan } from "./UpgradeGate";
import { UpgradeNudge } from "./UpgradeNudge";
import { api, auth } from "../lib/api";
import { toast } from "sonner";
import {
  LayoutDashboard, Radio as WarRoomIcon, Megaphone, Tv, Monitor,
  ListChecks, Layers, Vote, Users2, Database, MapPinned, Compass,
  HandCoins, LayoutTemplate, Wand2, ScrollText, LineChart, Activity,
  ShieldCheck, FileCheck2, Landmark, Wallet, Users, Settings, LogOut,
  Search, ChevronDown, Sparkles, Radio, Building2, Send, HeartHandshake,
  Receipt, FileText, Globe, Clapperboard, HelpCircle, Gauge, KeyRound, Loader2, X,
  Mail, Mailbox, ArrowUpRight, Route, BrainCircuit, CalendarDays, QrCode, TrendingUp, Briefcase, Palette, MessageSquare, Trophy
} from "lucide-react";

const NAV_GROUPS = [
  {
    label: "Overview",
    items: [
      { to: "/dashboard", icon: LayoutDashboard, label: "Mission Control", testid: "nav-home" },
      { to: "/onboarding/checklist", icon: Sparkles, label: "Launch Checklist", testid: "nav-onboarding-checklist", badge: "New" },
      { to: "/ask-totalreach", icon: Sparkles, label: "Ask TotalReach", testid: "nav-ask-totalreach", badge: "AI" },
      { to: "/race-intelligence", icon: Landmark, label: "Race Intelligence", testid: "nav-race-intelligence", badge: "New" },
      { to: "/calendar", icon: CalendarDays, label: "Campaign Calendar", testid: "nav-calendar", badge: "New" },
      { to: "/team", icon: Users, label: "Team", testid: "nav-team" },
      { to: "/war-room", icon: WarRoomIcon, label: "Campaign War Room", testid: "nav-war-room" },
    ],
  },
  {
    label: "Advertise",
    items: [
      { to: "/campaigns/new", icon: Megaphone, label: "Create Campaign", testid: "nav-create-campaign" },
      { to: "/campaigns/ctv/new", icon: Tv, label: "CTV Campaigns", testid: "nav-ctv", badge: "Hero" },
      { to: "/planner/ctv", icon: Monitor, label: "CTV Voter Planner", testid: "nav-ctv-planner", badge: "New" },
      { to: "/campaigns", icon: ListChecks, label: "Campaigns", testid: "nav-campaigns" },
      { to: "/media-plans", icon: Layers, label: "Media Plans", testid: "nav-media-plans" },
      { to: "/email-campaigns", icon: Mail, label: "Email Campaigns", testid: "nav-email", badge: "New" },
      { to: "/direct-mail", icon: Mailbox, label: "Direct Mail", testid: "nav-direct-mail", badge: "New" },
      { to: "/journeys", icon: Route, label: "Journeys", testid: "nav-journeys", badge: "New" },
    ],
  },
  {
    label: "Target",
    items: [
      { to: "/target-voters", icon: Vote, label: "Target Voters", testid: "nav-target-voters" },
      { to: "/political-data", icon: Database, label: "Political Data", testid: "nav-political-data" },
      { to: "/audiences", icon: Users2, label: "My Audiences", testid: "nav-audiences" },
      { to: "/first-party", icon: FileText, label: "First-Party Data", testid: "nav-first-party" },
      { to: "/geo", icon: MapPinned, label: "District & Geo", testid: "nav-geo" },
      { to: "/other-targeting", icon: Compass, label: "Other Targeting", testid: "nav-other-targeting" },
    ],
  },
  {
    label: "Operate",
    items: [
      { to: "/operations", icon: ListChecks, label: "Tasks & Approvals", testid: "nav-operations", badge: "New" },
      { to: "/events", icon: CalendarDays, label: "Events", testid: "nav-events", badge: "New" },
      { to: "/volunteers", icon: HeartHandshake, label: "Volunteers", testid: "nav-volunteers" },
      { to: "/volunteers/leaderboard", icon: Trophy, label: "Volunteer Leaderboard", testid: "nav-volunteer-leaderboard", badge: "New" },
      { to: "/portfolio", icon: Building2, label: "Race Portfolio", testid: "nav-portfolio" },
      { to: "/agency", icon: Briefcase, label: "My Clients (Agency)", testid: "nav-agency", badge: "New" },
      { to: "/agency/team", icon: Users, label: "Agency Team & Access", testid: "nav-agency-team", badge: "New" },
      { to: "/brand-library", icon: Palette, label: "Brand Library", testid: "nav-brand-library", badge: "New" },
      { to: "/claims-vault", icon: FileCheck2, label: "Claims Vault", testid: "nav-claims-vault" },
      { to: "/qr-codes", icon: QrCode, label: "QR Codes", testid: "nav-qr-codes", badge: "New" },
      { to: "/ad-library", icon: Megaphone, label: "Political Ad Library", testid: "nav-ad-library" },
      { to: "/election-night", icon: Vote, label: "Election Night", testid: "nav-election-night" },
    ],
  },
  {
    label: "Fundraise",
    items: [
      { to: "/fundraising", icon: HandCoins, label: "Fundraising Dashboard", testid: "nav-fundraising" },
      { to: "/fundraising/campaigns", icon: HeartHandshake, label: "Fundraising Campaigns", testid: "nav-fr-campaigns" },
      { to: "/fundraising/landing-pages", icon: LayoutTemplate, label: "Landing Pages", testid: "nav-landing-pages" },
      { to: "/fundraising/microsites", icon: Globe, label: "Microsites", testid: "nav-microsites" },
      { to: "/fundraising/donors", icon: Users, label: "Donors", testid: "nav-donors" },
      { to: "/fundraising/transactions", icon: Receipt, label: "Transactions", testid: "nav-transactions" },
      { to: "/fundraising/economics", icon: TrendingUp, label: "Source Economics", testid: "nav-source-economics", badge: "New" },
      { to: "/fundraising/sms", icon: MessageSquare, label: "Text-to-Give SMS", testid: "nav-sms", badge: "New" },
    ],
  },
  {
    label: "Create",
    items: [
      { to: "/ad-studio", icon: Clapperboard, label: "Political Ad Studio", testid: "nav-ad-studio", badge: "New" },
      { to: "/ad-studio/refreshes", icon: Sparkles, label: "Fresh Creative Ideas", testid: "nav-ad-refreshes", badge: "AI" },
      { to: "/ai-assistant", icon: Sparkles, label: "AI Campaign Assistant", testid: "nav-ai-assistant" },
      { to: "/creative-studio", icon: Wand2, label: "AI Creative Studio", testid: "nav-creative-studio" },
      { to: "/ai-landing-page", icon: LayoutTemplate, label: "AI Landing Page Builder", testid: "nav-ai-landing" },
      { to: "/creatives", icon: Send, label: "Creatives Library", testid: "nav-creatives" },
    ],
  },
  {
    label: "Intelligence",
    items: [
      { to: "/political-intelligence", icon: BrainCircuit, label: "Political Intelligence", testid: "nav-political-intelligence", badge: "New" },
      { to: "/midterms", icon: Landmark, label: "Midterm Directory", testid: "nav-midterms", badge: "FEC" },
      { to: "/intelligence", icon: Activity, label: "Polling & Sentiment", testid: "nav-political" },
      { to: "/opponent", icon: Vote, label: "Opponent Intelligence", testid: "nav-opponent" },
    ],
  },
  {
    label: "Measure",
    items: [
      { to: "/optimize", icon: Gauge, label: "Optimization Agent", testid: "nav-optimize", badge: "New" },
      { to: "/reports", icon: LineChart, label: "Advertising Reports", testid: "nav-reports" },
      { to: "/reports/ctv", icon: Tv, label: "CTV Reports", testid: "nav-ctv-reports" },
    ],
  },
  {
    label: "Compliance",
    items: [
      { to: "/compliance", icon: ShieldCheck, label: "Compliance Center", testid: "nav-compliance" },
      { to: "/audit", icon: ScrollText, label: "Political Audit Vault", testid: "nav-audit" },
    ],
  },
  {
    label: "Account",
    items: [
      { to: "/organizations", icon: Building2, label: "Political Organizations", testid: "nav-orgs" },
      { to: "/billing", icon: Wallet, label: "Billing", testid: "nav-billing" },
      { to: "/pricing/admin", icon: Landmark, label: "Pricing Engine", testid: "nav-pricing", role: "admin" },
      { to: "/ad-platform", icon: Layers, label: "Ad Platform (Admin)", testid: "nav-ad-platform", role: "admin" },
      { to: "/admin", icon: Settings, label: "Users & Settings", testid: "nav-admin" },
      { to: "/help", icon: HelpCircle, label: "Help & Glossary", testid: "nav-help" },
    ],
  },
];

const ChangePasswordModal = ({ onClose }) => {
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (form.next !== form.confirm) { setError("New passwords don't match"); return; }
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/change-password", { current_password: form.current, new_password: form.next });
      toast.success("Password updated");
      onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not update password");
    } finally { setBusy(false); }
  };
  const field = "w-full bg-slate-900/60 border border-white/10 rounded-md px-3 py-2.5 text-sm text-white focus:border-cyan-400 focus:outline-none";
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} className="bg-[#0a0e1a] border border-white/10 rounded-lg max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="change-password-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl font-bold flex items-center gap-2"><KeyRound className="w-4 h-4 text-cyan-300" /> Change password</h3>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-3">
          <input type="password" value={form.current} onChange={(e) => setForm({ ...form, current: e.target.value })} className={field} placeholder="Current password" required data-testid="cp-current-input" />
          <input type="password" value={form.next} onChange={(e) => setForm({ ...form, next: e.target.value })} className={field} placeholder="New password (min 8 characters)" required minLength={8} data-testid="cp-new-input" />
          <input type="password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} className={field} placeholder="Confirm new password" required minLength={8} data-testid="cp-confirm-input" />
        </div>
        {error && <div className="mt-3 text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-md p-2.5" data-testid="cp-error">{error}</div>}
        <button type="submit" disabled={busy} data-testid="cp-submit-btn"
          className="mt-4 w-full bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-900 font-semibold rounded-full px-4 py-2.5 text-sm hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Update password
        </button>
      </form>
    </div>
  );
};

const AI_ROUTES = ["/campaign-agent", "/creative-studio", "/ad-studio", "/ai-assistant", "/ai-landing-page"];

const SEARCH_TYPE_STYLES = {
  "Candidate": "text-cyan-300 border-cyan-400/30",
  "Donor": "text-emerald-300 border-emerald-400/30",
  "Campaign": "text-fuchsia-300 border-fuchsia-400/30",
  "Tracked race": "text-amber-300 border-amber-400/30",
};

export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = auth.getUser();
  const plan = usePlan();
  const [aiUsage, setAiUsage] = useState(null);
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  React.useEffect(() => {
    if (search.trim().length < 2) { setSearchResults(null); return undefined; }
    const t = setTimeout(() => {
      api.get(`/search/suggest?q=${encodeURIComponent(search.trim())}`)
        .then(r => setSearchResults(r.data.results))
        .catch(() => setSearchResults(null));
    }, 250);
    return () => clearTimeout(t);
  }, [search]);

  React.useEffect(() => {
    api.get("/pricing/ai-usage/me").then(r => setAiUsage(r.data)).catch(() => {});
  }, [location.pathname]);

  const handleLogout = () => {
    auth.logout();
    navigate("/login");
  };

  if (!user) {
    navigate("/login");
    return null;
  }

  const isAdmin = user.role === "super_admin" || user.role === "admin";

  return (
    <div className="flex h-screen bg-[#0A0E1A] text-white overflow-hidden">
      <aside className="w-64 shrink-0 border-r border-white/10 bg-[#070b15] flex flex-col" data-testid="sidebar">
        <div className="h-16 flex items-center px-5 border-b border-white/10">
          <TotalReachLogo />
        </div>
        <div className="pt-3">
          <OrgSwitcher />
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-4">
          {NAV_GROUPS.map((group) => (
            <div key={group.label}>
              <div className="px-2 mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                {group.label}
              </div>
              <div className="space-y-0.5">
                {group.items
                  .filter((it) => !it.role || isAdmin)
                  .map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.to === "/"}
                      data-testid={item.testid}
                      className={({ isActive }) =>
                        `group flex items-center gap-2.5 px-2.5 py-2 rounded-md text-sm transition-colors ${
                          isActive
                            ? "bg-gradient-to-r from-cyan-400/15 to-blue-500/10 text-cyan-300 border border-cyan-400/20"
                            : "text-slate-300 hover:bg-white/5 hover:text-cyan-300 border border-transparent"
                        }`
                      }
                    >
                      <item.icon className="w-4 h-4 shrink-0" />
                      <span className="truncate flex-1">{item.label}</span>
                      {item.badge && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-900 font-bold">
                          {item.badge}
                        </span>
                      )}
                    </NavLink>
                  ))}
              </div>
            </div>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="w-full flex items-center gap-2 px-2.5 py-2 rounded-md text-sm text-slate-300 hover:bg-rose-500/10 hover:text-rose-300 transition-colors"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col overflow-hidden">
        {plan?.gated && plan?.no_subscription && (
          <div className="bg-gradient-to-r from-amber-500/15 to-amber-400/5 border-b border-amber-400/20 px-6 py-2 flex items-center justify-between gap-3" data-testid="upgrade-banner">
            <span className="text-xs text-amber-200">You're on the <strong>Starter preview</strong> — subscribe to unlock CTV, AI, Email, Direct Mail and the full political suite.</span>
            <button onClick={() => navigate("/billing")} data-testid="upgrade-banner-btn"
              className="text-xs font-semibold text-amber-300 hover:text-amber-100 inline-flex items-center gap-1 whitespace-nowrap">
              View plans <ArrowUpRight className="w-3 h-3" />
            </button>
          </div>
        )}
        <header className="h-16 border-b border-white/10 bg-[#0A0E1A]/70 backdrop-blur-xl sticky top-0 z-30 flex items-center px-6 gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onBlur={() => setTimeout(() => setSearchResults(null), 150)}
              placeholder="Search voters, campaigns, donors…"
              data-testid="global-search"
              className="w-full bg-white/5 border border-white/10 rounded-full pl-10 pr-4 py-2 text-sm placeholder-slate-500 focus:border-cyan-400/50 focus:outline-none"
            />
            {searchResults && (
              <div className="absolute top-full mt-2 left-0 right-0 bg-[#0d1322] border border-white/10 rounded-2xl shadow-2xl overflow-hidden z-50 max-h-96 overflow-y-auto" data-testid="global-search-results">
                {searchResults.length === 0 ? (
                  <div className="px-4 py-3 text-xs text-slate-500">No matches for “{search}”</div>
                ) : searchResults.map((r, i) => (
                  <button key={i} data-testid={`search-result-${i}`}
                    onMouseDown={() => { navigate(r.link); setSearch(""); setSearchResults(null); }}
                    className="w-full text-left px-4 py-2.5 hover:bg-white/5 flex items-center gap-3 transition-colors">
                    <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border shrink-0 ${SEARCH_TYPE_STYLES[r.type] || "text-slate-400 border-white/15"}`}>{r.type}</span>
                    <span className="min-w-0">
                      <span className="block text-sm text-white truncate">{r.label}</span>
                      {r.sublabel && <span className="block text-[11px] text-slate-500 truncate">{r.sublabel}</span>}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="ml-auto flex items-center gap-3">
            {aiUsage && (
              <button onClick={() => navigate("/billing")} data-testid="ai-usage-meter"
                title={`${aiUsage.calls} AI calls this month, billed at +${aiUsage.ai_premium_pct}% premium`}
                className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 hover:border-cyan-400/30 transition-colors text-xs">
                <Sparkles className="w-3 h-3 text-cyan-300" />
                <span className="text-slate-400">AI this mo:</span>
                <span className="text-white font-semibold">${Number(aiUsage.billed).toFixed(2)}</span>
              </button>
            )}
            <span className="hidden md:inline-flex pill pill-active">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 pulse-glow" /> Political mode
            </span>
            <div className="relative">
              <button onClick={() => setMenuOpen(!menuOpen)} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 hover:border-cyan-400/30 transition-colors" data-testid="user-menu">
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center text-xs font-bold text-slate-900">
                  {(user.name || user.email).substring(0, 2).toUpperCase()}
                </div>
                <div className="hidden md:block text-left">
                  <div className="text-xs font-medium text-white leading-tight">{user.name}</div>
                  <div className="text-[10px] text-slate-400 leading-tight capitalize">{user.role?.replace(/_/g, " ")}</div>
                </div>
                <ChevronDown className={`w-3 h-3 text-slate-400 transition-transform ${menuOpen ? "rotate-180" : ""}`} />
              </button>
              {menuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
                  <div className="absolute right-0 top-full mt-2 w-52 rounded-lg border border-white/10 bg-[#0a0e1a] shadow-2xl z-50 py-1.5" data-testid="user-menu-dropdown">
                    <div className="px-3 py-2 border-b border-white/5">
                      <div className="text-xs font-medium text-white">{user.name}</div>
                      <div className="text-[10px] text-slate-500 truncate">{user.email}</div>
                    </div>
                    <button onClick={() => { setMenuOpen(false); setChangingPassword(true); }} data-testid="menu-change-password"
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:bg-white/5 hover:text-cyan-300 transition-colors">
                      <KeyRound className="w-3.5 h-3.5" /> Change password
                    </button>
                    <button onClick={handleLogout} data-testid="menu-sign-out"
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:bg-rose-500/10 hover:text-rose-300 transition-colors">
                      <LogOut className="w-3.5 h-3.5" /> Sign out
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto" data-testid="main-content">
          <Outlet />
        </main>
      </div>
      {changingPassword && <ChangePasswordModal onClose={() => setChangingPassword(false)} />}
      <UpgradeNudge />
      <AskTotalReachWidget />
      {(!plan?.gated || plan?.features?.ai) && AI_ROUTES.some(r => location.pathname.startsWith(r)) && (
        <AiDisclaimerGate onDecline={() => navigate("/dashboard")} />
      )}
    </div>
  );
}
